import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, Grid, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, LocalHospital as DiagnosisIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadDiagnoses, getDiagnosisInsurances,
  updateInsuredDiagnosis, deleteInsuredDiagnosis, saveNewDiagnosis,
  bulkApplyDiagnosisInsurance,
} from '../BaseURL';

/* ─────────────────────────────────────────────────────────────
   Shared theme tokens — every sub-component pulls colors from
   here so the page renders cleanly on both the default light
   theme and the black/dark theme.
───────────────────────────────────────────────────────────── */
const useDxTokens = () => {
  const theme = useTheme();
  return useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      isDark,
      pageBg:      isDark ? '#000000' : '#F4F6FA',
      surface:     isDark ? '#0E0E14' : '#FFFFFF',
      surfaceAlt:  isDark ? '#14141C' : '#F8FAFC',
      fieldBg:     isDark ? '#14141C' : '#FFFFFF',
      borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
      borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
      text:        isDark ? '#FFFFFF' : '#0F172A',
      textMuted:   isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
      textDim:     isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
      rowOdd:      isDark ? 'rgba(255,255,255,0.02)' : '#F9FAFF',
      rowEven:     isDark ? 'transparent' : '#FFFFFF',
      rowHover:    isDark ? 'rgba(98,114,234,0.10)' : 'rgba(102,126,234,0.04)',
      rowSelected: isDark ? 'rgba(98,114,234,0.18)' : 'rgba(102,126,234,0.12)',
      cardShadow:  isDark
        ? '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.35)'
        : '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
      // Accent palette — translucent variants are computed at usage sites.
      accent: {
        primary: '#6272ea',   // indigo (replacing the old #667eea but readable on black)
        success: '#10b981',
        danger:  '#ef4444',
        warning: '#f59e0b',
        teal:    '#11998e'    // brand teal preserved for header gradient
      }
    };
  }, [theme.palette.mode]);
};

/* ─────────────────────────────────────────────────────────────
   Editable table row with checkbox selection
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const t = useDxTokens();
  const cellInputSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.78rem',
      backgroundColor: t.fieldBg,
      '& input': { py: '4px', px: '8px', color: t.text },
      '& fieldset': { borderColor: t.borderSubtle },
      '&:hover fieldset': { borderColor: t.accent.primary },
      '&.Mui-focused fieldset': { borderColor: t.accent.primary },
    },
  };
  const cellSelectSx = {
    fontSize: '0.78rem',
    backgroundColor: t.fieldBg,
    color: t.text,
    '& .MuiSelect-select': { py: '4px', px: '8px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: t.borderSubtle },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.accent.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent.primary },
  };
  const tdSx = { borderBottom: `1px solid ${t.borderSubtle}`, py: 0.75, px: 1, color: t.text };
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)':  { background: selected ? t.rowSelected : t.rowOdd },
      '&:nth-of-type(even)': { background: selected ? t.rowSelected : t.rowEven },
      '&:hover': { background: selected ? t.rowSelected : t.rowHover },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onSelectToggle(row.id)}
          sx={{
            color: t.accent.primary,
            '&.Mui-checked': { color: t.accent.primary },
            p: 0.5
          }}
        />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: t.textDim, fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: t.accent.primary }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.tariff ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('tariff', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.topup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('topup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age_cat || ''} onChange={e => onChange('age_cat', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: t.textDim, fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.icd ?? ''} onChange={e => onChange('icd', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.core ?? ''} onChange={e => onChange('core', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 120 }}>
        <TextField size="small" fullWidth value={row.spe ?? ''} onChange={e => onChange('spe', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.spe_code ?? ''} onChange={e => onChange('spe_code', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{
              fontSize: '0.7rem', px: 1.5, py: 0.5, fontWeight: 600, textTransform: 'none', borderRadius: 1,
              background: `linear-gradient(135deg, ${t.accent.success} 0%, ${alpha(t.accent.success, 0.82)} 100%)`,
              boxShadow: `0 2px 8px ${alpha(t.accent.success, 0.3)}`,
              '&:hover': { boxShadow: `0 4px 14px ${alpha(t.accent.success, 0.45)}` },
              '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
            }}>
            {updating ? 'Saving...' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{
              fontSize: '0.7rem', px: 1.5, py: 0.5, fontWeight: 600, textTransform: 'none', borderRadius: 1,
              background: `linear-gradient(135deg, ${t.accent.danger} 0%, ${alpha(t.accent.danger, 0.82)} 100%)`,
              boxShadow: `0 2px 8px ${alpha(t.accent.danger, 0.3)}`,
              '&:hover': { boxShadow: `0 4px 14px ${alpha(t.accent.danger, 0.45)}` },
              '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
            }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   Bulk Apply Dialog - Apply selected row to multiple insurances
───────────────────────────────────────────────────────────── */
const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const t = useDxTokens();
  const [saving, setSaving] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  useEffect(() => {
    if (open) {
      // Reset selections when dialog opens
      setSelectedInsurances({});
      setInsuranceSearch('');
    }
  }, [open]);

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { 
      // Only select if not already assigned
      if (!existingStatuses.includes(i.name)) {
        all[i.id] = true; 
      }
    });
    setSelectedInsurances(all);
  };

  const handleDeselectAll = () => setSelectedInsurances({});

  const handleApply = async () => {
    if (selectedCount === 0) {
      await Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one insurance provider to apply this diagnosis to.',
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age_cat: sourceRow.age_cat,
          name: sourceRow.name,
          cat: sourceRow.cat,
          icd: sourceRow.icd,
          core: sourceRow.core,
          tariff: parseFloat(sourceRow.tariff),
          topup: parseFloat(sourceRow.topup),
          spe: sourceRow.spe,
          spe_code: sourceRow.spe_code,
          status: provider?.name || ''
        };
      });

      const response = await axios.post(bulkApplyDiagnosisInsurance, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Applied!',
          html: `<b>${sourceRow.name}</b> applied to <b>${selectedCount}</b> insurance provider${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Apply failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Apply Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while applying',
        icon: 'error',
        confirmButtonColor: '#e53935',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedInsurances({});
    setInsuranceSearch('');
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssignedProviders = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: {
        borderRadius: 3, overflow: 'hidden',
        backgroundColor: t.pageBg,
        backgroundImage: 'none',
        border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
        boxShadow: t.isDark ? '0 24px 64px rgba(0,0,0,0.7)' : '0 24px 64px rgba(15,23,42,0.18)'
      } }}>

      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.78)} 100%)`,
        color: 'white', p: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            backgroundColor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}>
            <CopyIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Copy this diagnosis to additional providers</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.18)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
          position: 'relative', zIndex: 1
        }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: t.pageBg }}>
        {/* Source Row Info */}
        <Paper elevation={0} sx={{
          p: 2, mb: 3,
          backgroundColor: t.surface,
          backgroundImage: 'none',
          border: `1px solid ${alpha(t.accent.primary, 0.25)}`,
          borderRadius: 2,
          boxShadow: t.cardShadow
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.primary, boxShadow: `0 0 12px ${alpha(t.accent.primary, 0.5)}` }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: 1 }}>
              Source Diagnosis Details
            </Typography>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.88rem', color: t.text }}>
                <Box component="span" sx={{ fontWeight: 700 }}>Name:</Box> {sourceRow.name}
              </Typography>
            </Grid>
            {[
              ['ICD',      sourceRow.icd || 'N/A'],
              ['Core',     sourceRow.core || 'N/A'],
              ['Tariff',   `GH¢ ${sourceRow.tariff || 0}`],
              ['Top-up',   `GH¢ ${sourceRow.topup || 0}`],
              ['Age',      sourceRow.age_cat || 'N/A'],
              ['Specialty',sourceRow.spe_code || 'N/A']
            ].map(([label, val]) => (
              <Grid item xs={6} key={label}>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: t.textMuted }}>
                  <Box component="span" sx={{ fontWeight: 700, color: t.text }}>{label}:</Box> {val}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Insurance Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
              Select Insurance Providers
            </Typography>
            <Typography variant="caption" sx={{ color: t.textMuted }}>
              {selectedCount} of {availableProviders.length} available selected
            </Typography>
          </Box>
          {selectedCount > 0 && (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: `${t.accent.success} !important` }} />}
              label={`${selectedCount} selected`}
              sx={{
                background: alpha(t.accent.success, t.isDark ? 0.18 : 0.14),
                color: t.accent.success,
                fontWeight: 700, fontSize: '0.72rem',
                border: `1px solid ${alpha(t.accent.success, 0.3)}`
              }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}
            sx={{ borderColor: alpha(t.accent.primary, 0.4), color: t.accent.primary, fontSize: '0.74rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>
            Select All Available
          </Button>
          <Button size="small" variant="outlined" onClick={handleDeselectAll}
            sx={{ borderColor: alpha(t.accent.primary, 0.4), color: t.accent.primary, fontSize: '0.74rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>
            Deselect All
          </Button>
        </Box>

        <TextField fullWidth size="small" placeholder="Search insurance…"
          value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: t.fieldBg, borderRadius: 1.5 } }}
          InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: t.textMuted }} /></InputAdornment>) }} />

        <Box sx={{
          border: `1px solid ${t.borderSubtle}`, borderRadius: 2,
          backgroundColor: t.surface,
          maxHeight: 320, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' },
        }}>
          {loadingInsurance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={28} sx={{ color: t.accent.primary }} />
            </Box>
          ) : (
            <>
              {/* Available Providers */}
              {availableProviders.length > 0 && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: t.accent.success, display: 'block', mb: 0.75, px: 1, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                    Available ({availableProviders.length})
                  </Typography>
                  {availableProviders.map(insurance => (
                    <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '6px 8px', borderRadius: 1,
                        cursor: 'pointer', mb: 0.5,
                        backgroundColor: selectedInsurances[insurance.id] ? alpha(t.accent.primary, 0.12) : 'transparent',
                        border: `1px solid ${selectedInsurances[insurance.id] ? alpha(t.accent.primary, 0.3) : 'transparent'}`,
                        transition: 'all 0.15s ease',
                        '&:hover': { backgroundColor: alpha(t.accent.primary, 0.08) },
                      }}>
                      <Checkbox size="small"
                        checked={selectedInsurances[insurance.id] || false}
                        onChange={() => handleInsuranceToggle(insurance.id)}
                        onClick={e => e.stopPropagation()}
                        sx={{ p: 0.5, color: t.accent.primary, '&.Mui-checked': { color: t.accent.primary } }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem', color: t.text }}>
                        {insurance.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Already Assigned Providers */}
              {alreadyAssignedProviders.length > 0 && (
                <Box sx={{ p: 1, borderTop: availableProviders.length > 0 ? `1px solid ${t.borderSubtle}` : 'none' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: t.accent.danger, display: 'block', mb: 0.75, px: 1, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                    Already Assigned ({alreadyAssignedProviders.length})
                  </Typography>
                  {alreadyAssignedProviders.map(insurance => (
                    <Box key={insurance.id}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '6px 8px', borderRadius: 1,
                        mb: 0.5,
                        backgroundColor: alpha(t.accent.danger, t.isDark ? 0.08 : 0.05),
                        opacity: 0.7,
                        border: `1px solid ${alpha(t.accent.danger, 0.15)}`,
                      }}>
                      <Checkbox size="small" disabled checked={false} sx={{ p: 0.5 }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem', color: t.textMuted }}>
                        {insurance.name}
                      </Typography>
                      <Chip label="Exists" size="small" sx={{
                        ml: 'auto', fontSize: '0.65rem', height: 20, fontWeight: 700,
                        backgroundColor: alpha(t.accent.danger, t.isDark ? 0.18 : 0.14),
                        color: t.accent.danger,
                        border: `1px solid ${alpha(t.accent.danger, 0.3)}`
                      }} />
                    </Box>
                  ))}
                </Box>
              )}

              {availableProviders.length === 0 && alreadyAssignedProviders.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                  <Typography variant="body2" sx={{ color: t.textMuted }}>No providers found</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        borderTop: `1px solid ${t.borderSubtle}`,
        backgroundColor: t.surface,
        gap: 1
      }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{
            color: t.text,
            borderColor: t.borderStrong,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: t.rowHover, borderColor: t.borderStrong }
          }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CopyIcon />}
          onClick={handleApply} disabled={saving || selectedCount === 0}
          sx={{
            background: `linear-gradient(135deg, ${t.accent.primary} 0%, ${alpha(t.accent.primary, 0.82)} 100%)`,
            color: 'white', fontWeight: 700, px: 3, textTransform: 'none',
            boxShadow: `0 6px 16px ${alpha(t.accent.primary, 0.35)}`,
            '&:hover': { boxShadow: `0 8px 22px ${alpha(t.accent.primary, 0.45)}` },
            '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' },
          }}>
          {saving ? 'Applying…' : `Apply to ${selectedCount} Provider${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Diagnosis Dialog
───────────────────────────────────────────────────────────── */
const NewDiagnosisDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const t = useDxTokens();
  const fieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: t.fieldBg, borderRadius: 1.5 } };
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [ageCategory, setAgeCategory] = useState('');
  const [diagnosisName, setDiagnosisName] = useState('');
  const [category, setCategory] = useState('');
  const [icdCode, setIcdCode] = useState('');
  const [corePackage, setCorePackage] = useState('');
  const [tariff, setTariff] = useState('');
  const [topUp, setTopUp] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!ageCategory)
      e.ageCategory = 'Age Category is required';

    if (!diagnosisName.trim())
      e.diagnosisName = 'Diagnosis Name is required';

    if (!category.trim())
      e.category = 'Category is required';

    if (!icdCode.trim())
      e.icdCode = 'ICD Code is required';

    if (!corePackage.trim())
      e.corePackage = 'Core Package is required';

    // Tariff: required, must be a number ≥ 0
    if (tariff === '' || tariff === null || tariff === undefined)
      e.tariff = 'Tariff is required';
    else if (isNaN(parseFloat(tariff)) || parseFloat(tariff) < 0)
      e.tariff = 'Must be a valid number (0 or more)';

    // Top-up: required, must be a number ≥ 0
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialty.trim())
      e.specialty = 'Specialty is required';

    if (!specialtyCode.trim())
      e.specialtyCode = 'Specialty Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      const pending = {
        ...(!ageCategory ? { a: 'Age Category must be selected' } : {}),
        ...(!diagnosisName.trim() ? { b: 'Diagnosis Name must be entered' } : {}),
        ...(!category.trim() ? { c: 'Category must be entered' } : {}),
        ...(!icdCode.trim() ? { d: 'ICD Code must be entered' } : {}),
        ...(!corePackage.trim() ? { e: 'Core Package must be entered' } : {}),
        ...(tariff === '' ? { f: 'Tariff must be entered' } : {}),
        ...(topUp === '' ? { g: 'Top-up must be entered' } : {}),
        ...(!specialty.trim() ? { h: 'Specialty must be entered' } : {}),
        ...(!specialtyCode.trim() ? { i: 'Specialty Code must be entered' } : {}),
        ...(selectedCount === 0 ? { j: 'At least one insurance must be selected' } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age_cat: ageCategory,
          name: diagnosisName.trim(),
          cat: category.trim(),
          icd: icdCode.trim(),
          core: corePackage.trim(),
          tariff: parseFloat(tariff),
          topup: parseFloat(topUp),
          spe: specialty.trim(),
          spe_code: specialtyCode.trim(),
          status: provider?.name || ''
        };
      });

      const response = await axios.post(saveNewDiagnosis, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${diagnosisName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#e53935',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setAgeCategory(''); setDiagnosisName(''); setCategory('');
    setIcdCode(''); setCorePackage(''); setTariff(''); setTopUp('');
    setSpecialty(''); setSpecialtyCode('');
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: {
        borderRadius: 3, overflow: 'hidden',
        backgroundColor: t.pageBg,
        backgroundImage: 'none',
        border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
        boxShadow: t.isDark ? '0 24px 64px rgba(0,0,0,0.7)' : '0 24px 64px rgba(15,23,42,0.18)'
      } }}>

      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.78)} 100%)`,
        color: 'white', p: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            backgroundColor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}>
            <DiagnosisIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>New Diagnosis</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Configure diagnosis details and assign insurance providers</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.18)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
          position: 'relative', zIndex: 1
        }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: t.pageBg }}>
        <Grid container sx={{ minHeight: 520 }} columns={{ xs: 4, sm: 8, md: 12 }}>

          {/* ── LEFT: diagnosis fields ───────────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{
            p: 3,
            borderRight: { md: `1px solid ${t.borderSubtle}` },
            backgroundColor: t.surfaceAlt
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.primary, boxShadow: `0 0 12px ${alpha(t.accent.primary, 0.5)}` }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.74rem' }}>
                Diagnosis Details
              </Typography>
            </Box>

            {/* Age Category */}
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.ageCategory ? t.accent.danger : t.textMuted, mb: 0.5, display: 'block' }}>
                Age Category *
              </Typography>
              <RadioGroup row value={ageCategory}
                onChange={e => { setAgeCategory(e.target.value); clearError('ageCategory'); }}>
                {['Adult', 'Child', 'Both'].map(v => (
                  <FormControlLabel key={v} value={v}
                    control={<Radio size="small" sx={{ color: t.accent.primary, '&.Mui-checked': { color: t.accent.primary } }} />}
                    label={<Typography variant="body2" sx={{ color: t.text }}>{v}</Typography>} />
                ))}
              </RadioGroup>
              {errors.ageCategory && <Typography variant="caption" sx={{ color: t.accent.danger, fontSize: '0.7rem' }}>{errors.ageCategory}</Typography>}
            </FormControl>

            <Divider sx={{ my: 1.5, borderColor: t.borderSubtle }} />

            <TextField fullWidth size="small" label="Diagnosis Name *" value={diagnosisName}
              onChange={e => { setDiagnosisName(e.target.value); clearError('diagnosisName'); }}
              error={!!errors.diagnosisName} helperText={errors.diagnosisName} sx={{ mb: 2, ...fieldSx }} />

            <TextField fullWidth size="small" label="Category *" value={category}
              onChange={e => { setCategory(e.target.value); clearError('category'); }}
              error={!!errors.category} helperText={errors.category} sx={{ mb: 2, ...fieldSx }}
              placeholder="e.g., CARDIAC DISEASES, MALNUTRITION" />

            <TextField fullWidth size="small" label="ICD Code *" value={icdCode}
              onChange={e => { setIcdCode(e.target.value); clearError('icdCode'); }}
              error={!!errors.icdCode} helperText={errors.icdCode} sx={{ mb: 2, ...fieldSx }}
              placeholder="e.g., I50.1, E43" />

            <TextField fullWidth size="small" label="Core Package/Code *" value={corePackage}
              onChange={e => { setCorePackage(e.target.value); clearError('corePackage'); }}
              error={!!errors.corePackage} helperText={errors.corePackage} sx={{ mb: 2, ...fieldSx }}
              placeholder="e.g., MEDI07A, PAED10C" />

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Tariff GH¢ *" type="number"
                  value={tariff}
                  onChange={e => { setTariff(e.target.value); clearError('tariff'); }}
                  error={!!errors.tariff} helperText={errors.tariff}
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Top-up GH¢ *" type="number"
                  value={topUp}
                  onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                  error={!!errors.topUp} helperText={errors.topUp}
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={fieldSx} />
              </Grid>
            </Grid>

            <TextField fullWidth size="small" label="Specialty *" value={specialty}
              onChange={e => { setSpecialty(e.target.value); clearError('specialty'); }}
              error={!!errors.specialty} helperText={errors.specialty} sx={{ mb: 2, ...fieldSx }}
              placeholder="e.g., PAEDIATRICS, ADULT MEDICINE" />

            <TextField fullWidth size="small" label="Specialty Code *" value={specialtyCode}
              onChange={e => { setSpecialtyCode(e.target.value); clearError('specialtyCode'); }}
              error={!!errors.specialtyCode} helperText={errors.specialtyCode}
              placeholder="e.g., PAED, MEDI"
              sx={fieldSx} />
          </Grid>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, display: 'flex', flexDirection: 'column', backgroundColor: t.surface }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: errors.insurance ? t.accent.danger : t.accent.success, boxShadow: `0 0 12px ${alpha(errors.insurance ? t.accent.danger : t.accent.success, 0.5)}` }} />
                <Box>
                  <Typography variant="subtitle2"
                    sx={{ fontWeight: 700, color: errors.insurance ? t.accent.danger : t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
                    Insurance Providers *
                  </Typography>
                  <Typography variant="caption" sx={{ color: errors.insurance ? t.accent.danger : t.textMuted }}>
                    {selectedCount} of {insuranceProviders.length} selected
                  </Typography>
                </Box>
              </Box>
              {selectedCount > 0 && (
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: `${t.accent.success} !important` }} />}
                  label={`${selectedCount} selected`}
                  sx={{
                    background: alpha(t.accent.success, t.isDark ? 0.18 : 0.14),
                    color: t.accent.success,
                    fontWeight: 700, fontSize: '0.72rem',
                    border: `1px solid ${alpha(t.accent.success, 0.3)}`
                  }} />
              )}
            </Box>

            {errors.insurance && (
              <Typography variant="caption" sx={{ color: t.accent.danger, fontSize: '0.7rem', mb: 0.5 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: 0.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}
                sx={{ borderColor: alpha(t.accent.primary, 0.4), color: t.accent.primary, fontSize: '0.74rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}
                sx={{ borderColor: alpha(t.accent.primary, 0.4), color: t.accent.primary, fontSize: '0.74rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>
                Deselect All
              </Button>
            </Box>

            <TextField fullWidth size="small" placeholder="Search insurance…"
              value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)}
              sx={{ mb: 1.5, ...fieldSx }}
              InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: t.textMuted }} /></InputAdornment>) }} />

            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: `1px solid ${errors.insurance ? t.accent.danger : t.borderSubtle}`,
              backgroundColor: t.surfaceAlt,
              borderRadius: 2, p: 1, minHeight: 280, maxHeight: 320,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={28} sx={{ color: t.accent.primary }} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" sx={{ color: t.textMuted }}>No providers found</Typography>
                </Box>
              ) : (
                filteredProviders.map(insurance => (
                  <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', p: '6px 8px', borderRadius: 1,
                      cursor: 'pointer', mb: 0.5,
                      backgroundColor: selectedInsurances[insurance.id] ? alpha(t.accent.primary, 0.12) : 'transparent',
                      border: `1px solid ${selectedInsurances[insurance.id] ? alpha(t.accent.primary, 0.3) : 'transparent'}`,
                      transition: 'all 0.15s ease',
                      '&:hover': { backgroundColor: alpha(t.accent.primary, 0.08) },
                    }}>
                    <Checkbox size="small"
                      checked={selectedInsurances[insurance.id] || false}
                      onChange={() => handleInsuranceToggle(insurance.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0.5, color: t.accent.primary, '&.Mui-checked': { color: t.accent.primary } }} />
                    <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem', color: t.text }}>
                      {insurance.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        borderTop: `1px solid ${t.borderSubtle}`,
        backgroundColor: t.surface,
        gap: 1
      }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{
            color: t.text,
            borderColor: t.borderStrong,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: t.rowHover, borderColor: t.borderStrong }
          }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{
            background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
            color: 'white', fontWeight: 700, px: 3, textTransform: 'none',
            boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.35)}`,
            '&:hover': { boxShadow: `0 8px 22px ${alpha(t.accent.teal, 0.45)}` },
            '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' },
          }}>
          {saving ? 'Saving…' : 'Save Diagnosis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const Diagnosis = () => {
  const t = useDxTokens();
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(false);
  const [loadingDiagnosisInsurances, setLoadingDiagnosisInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [diagnosisInsuranceRows, setDiagnosisInsuranceRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadDiagnosesFromBackend = async () => {
    setLoadingDiagnoses(true); setError(null);
    try {
      const res = await axios.get(loadDiagnoses);
      if (res.data.success) setDiagnoses(res.data.results);
      else setError('Failed to load diagnoses');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load diagnoses');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load diagnoses', icon: 'error' });
    } finally { setLoadingDiagnoses(false); }
  };

  const loadDiagnosisInsurancesForDisplay = async (diagnosisId, diagnosisName) => {
    setLoadingDiagnosisInsurances(true); setDiagnosisInsuranceRows([]); setSelectedRowIds([]);
    try {
      const res = await axios.post(getDiagnosisInsurances, { diagnosisId, diagnosisName });
      if (res.data.success) setDiagnosisInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load diagnosis insurances', icon: 'error' });
    } finally { setLoadingDiagnosisInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.tariff || !row.topup || !row.age_cat) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Tariff, Top-up, Age Category)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredDiagnosis, {
        id: row.id,
        tariff: parseFloat(row.tariff),
        topup: parseFloat(row.topup),
        age_cat: row.age_cat,
        icd: row.icd || '',
        core: row.core || '',
        spe: row.spe || '',
        spe_code: row.spe_code || ''
      });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedDiagnosis) await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.status || 'this entry'}</strong>?<br/><span style="color:#e53935;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredDiagnosis, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.status}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setDiagnosisInsuranceRows(prev => prev.filter(r => r.id !== row.id));
        setSelectedRowIds(prev => prev.filter(id => id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  const handleRowSelectionToggle = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllRows = () => {
    if (selectedRowIds.length === diagnosisInsuranceRows.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(diagnosisInsuranceRows.map(r => r.id));
    }
  };

  const handleBulkApply = () => {
    if (selectedRowIds.length !== 1) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please select exactly ONE row to use as template for bulk insurance assignment.',
        icon: 'info',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    const sourceRow = diagnosisInsuranceRows.find(r => r.id === selectedRowIds[0]);
    if (!sourceRow) return;

    setSourceRowForBulkApply(sourceRow);
    setBulkApplyDialogOpen(true);
  };

  useEffect(() => { loadInsuranceProviders(); loadDiagnosesFromBackend(); }, []);

  const handleDiagnosisClick = async (diagnosis) => {
    if (selectedDiagnosis?.id === diagnosis.id) { 
      setSelectedDiagnosis(null); 
      setDiagnosisInsuranceRows([]); 
      setSelectedRowIds([]);
      return; 
    }
    setSelectedDiagnosis(diagnosis);
    await loadDiagnosisInsurancesForDisplay(diagnosis.id, diagnosis.name);
  };

  const handleRowChange = (idx, field, value) =>
    setDiagnosisInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const existingStatuses = diagnosisInsuranceRows.map(r => r.status);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page header — compact, matches the rest of the admin panel */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
            color: 'white', boxShadow: '0 4px 12px rgba(17,153,142,0.35)',
          }}>
            <DiagnosisIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
              Diagnosis Tariff Management
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.25 }}>
              Pick a diagnosis on the left to view and edit its insurance coverage.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={loadDiagnosesFromBackend}
            disabled={loadingDiagnoses}
            startIcon={loadingDiagnoses ? <CircularProgress size={16} sx={{ color: 'text.secondary' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {loadingDiagnoses ? 'Loading…' : 'Reload Diagnoses'}
          </Button>
          <Button
            onClick={loadInsuranceProviders}
            disabled={loadingInsurance}
            startIcon={loadingInsurance ? <CircularProgress size={16} sx={{ color: 'text.secondary' }} /> : <CloudDownload sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {loadingInsurance ? 'Loading…' : 'Reload Insurance'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setDialogOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.75, px: 2.25,
              background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
              boxShadow: '0 4px 12px rgba(17,153,142,0.28)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)',
                boxShadow: '0 6px 16px rgba(17,153,142,0.4)',
              },
            }}
          >
            New Diagnosis
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            {/* Left: diagnosis list */}
            <Grid item xs={12} md={4} size={4}>
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', height: 580, display: 'flex', flexDirection: 'column' }}>
                <TextField fullWidth size="small" placeholder="Search diagnoses..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                  InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Available Diagnoses</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingDiagnoses && <CircularProgress size={16} />}
                    <Chip size="small" label={`${diagnoses.length} total`} sx={{ fontSize: '0.68rem', bgcolor: 'rgba(102,126,234,0.18)', color: '#a5b4fc', border: '1px solid rgba(165,180,252,0.30)' }} />
                  </Box>
                </Box>
                {selectedDiagnosis && (
                  <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'linear-gradient(90deg,rgba(102,126,234,0.12),rgba(56,239,125,0.08))', border: '1px solid rgba(102,126,234,0.30)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                    <Typography variant="caption" sx={{ color: '#a5b4fc', fontWeight: 700 }}>Selected: {selectedDiagnosis.name}</Typography>
                    <IconButton size="small" onClick={() => { setSelectedDiagnosis(null); setDiagnosisInsuranceRows([]); setSelectedRowIds([]); }} sx={{ ml: 'auto', p: 0.2, color: 'text.secondary' }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '10px' } }}>
                  {loadingDiagnoses ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                  ) : diagnoses.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
                      <DiagnosisIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">No diagnoses found. Click "Reload Diagnoses".</Typography>
                    </Box>
                  ) : (
                    diagnoses.filter(d => d.name?.toLowerCase().includes(searchTerm.toLowerCase())).map(diagnosis => {
                      const isActive = selectedDiagnosis?.id === diagnosis.id;
                      return (
                        <Box key={diagnosis.id} onClick={() => handleDiagnosisClick(diagnosis)}
                          sx={{
                            display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5,
                            cursor: 'pointer', transition: 'all 0.18s ease',
                            border: '1.5px solid',
                            borderColor: isActive ? '#a5b4fc' : 'transparent',
                            bgcolor: isActive ? 'rgba(102,126,234,0.12)' : 'transparent',
                            '&:hover': { bgcolor: 'rgba(102,126,234,0.06)', transform: 'translateX(3px)', borderColor: 'rgba(165,180,252,0.40)' },
                          }}>
                          <DiagnosisIcon sx={{ fontSize: 16, mr: 1, color: isActive ? '#a5b4fc' : 'text.disabled', transition: 'color 0.18s' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: isActive ? '#a5b4fc' : 'text.primary', fontWeight: isActive ? 700 : 400 }}>
                            {diagnosis.name}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Right: editable records table */}
            <Grid item xs={12} md={8} size={8}>
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', height: 580, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Insurance Coverage Records</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedDiagnosis ? `Records for: ${selectedDiagnosis.name}` : 'Select a diagnosis to load records'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && (
                      <Chip
                        size="small"
                        label={`${selectedRowIds.length} selected`}
                        sx={{ bgcolor: 'rgba(25,118,210,0.18)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.30)', fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    )}
                    {selectedDiagnosis && !loadingDiagnosisInsurances && (
                      <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${diagnosisInsuranceRows.length} record${diagnosisInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                        sx={{
                          bgcolor: diagnosisInsuranceRows.length > 0 ? 'rgba(21,128,61,0.18)' : 'rgba(220,38,38,0.16)',
                          color: diagnosisInsuranceRows.length > 0 ? '#4ade80' : '#f87171',
                          border: '1px solid',
                          borderColor: diagnosisInsuranceRows.length > 0 ? 'rgba(74,222,128,0.30)' : 'rgba(248,113,113,0.30)',
                          fontWeight: 700, fontSize: '0.7rem',
                        }} />
                    )}
                  </Box>
                </Box>

                {/* Bulk Action Bar */}
                {selectedDiagnosis && diagnosisInsuranceRows.length > 0 && (
                  <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSelectAllRows}
                      sx={{
                        textTransform: 'none', fontSize: '0.72rem', py: 0.4,
                        borderColor: 'rgba(165,180,252,0.40)', color: '#a5b4fc',
                        '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(102,126,234,0.10)' },
                      }}
                    >
                      {selectedRowIds.length === diagnosisInsuranceRows.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CopyIcon />}
                      onClick={handleBulkApply}
                      disabled={selectedRowIds.length !== 1}
                      sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.4, px: 1.5,
                        background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
                        boxShadow: '0 3px 10px rgba(17,153,142,0.28)',
                        '&:hover': { background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)' },
                        '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
                      }}
                    >
                      Apply to Insurances
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                      {selectedRowIds.length === 0 && 'Select one row to apply to multiple insurances'}
                      {selectedRowIds.length === 1 && 'Click "Apply to Insurances" to copy this row'}
                      {selectedRowIds.length > 1 && 'Select only ONE row to use as template'}
                    </Typography>
                  </Box>
                )}

                {!selectedDiagnosis ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.5 }}>
                    <InsuranceIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">Click any diagnosis on the left<br />to load its insurance records</Typography>
                  </Box>
                ) : loadingDiagnosisInsurances ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center">
                      <CircularProgress size={36} sx={{ color: '#a5b4fc' }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Loading records...</Typography>
                    </Box>
                  </Box>
                ) : diagnosisInsuranceRows.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, opacity: 0.6 }}>
                    <InsuranceIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary">No insurance records found for this diagnosis.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '10px' } }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', width: 48, textAlign: 'center', py: 1.2, px: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={selectedRowIds.length === diagnosisInsuranceRows.length && diagnosisInsuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < diagnosisInsuranceRows.length}
                              onChange={handleSelectAllRows}
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Age', 'ICD Code', 'Core', 'Specialty', 'Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.6px', textTransform: 'uppercase', borderBottom: '1px solid', borderColor: 'divider', py: 1.2, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 180, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diagnosisInsuranceRows.map((row, idx) => (
                          <EditableRow 
                            key={row.id || idx} 
                            row={row} 
                            idx={idx}
                            selected={selectedRowIds.includes(row.id)}
                            onSelectToggle={handleRowSelectionToggle}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} 
                            onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} 
                            deleting={deletingRowId === row.id} 
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <NewDiagnosisDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadDiagnosesFromBackend}
      />

      <BulkApplyDialog
        open={bulkApplyDialogOpen}
        onClose={() => {
          setBulkApplyDialogOpen(false);
          setSourceRowForBulkApply(null);
        }}
        sourceRow={sourceRowForBulkApply}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={() => {
          if (selectedDiagnosis) {
            loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
          }
        }}
        existingStatuses={existingStatuses}
      />
    </Box>
  );
};

export default Diagnosis;





